import type { FC, ReactNode } from 'react'
import { Box, Button, IconButton, Tooltip, useTheme } from '@mui/material'
import BlockIcon from '@mui/icons-material/Block'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import CheckIcon from '@mui/icons-material/Check'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import InfoIcon from '@mui/icons-material/Info'
import LaunchIcon from '@mui/icons-material/Launch'
import TimerIcon from '@mui/icons-material/Timer'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { format } from 'date-fns'
import { Link } from 'react-router'
import { useSnackbar } from 'notistack'
import { useActiveVideoVisit } from '~/shared/hooks/activeVideoVisit'
import { membersWebAppUrl } from '~/shared/lib/config'
import { DetailGrid, SectionCard, StatusPill, type DetailGridItem, type Tone } from '~/shared/ui'
import { getFormSubmissionStatus } from '~/features/member-profile/overview/forms'
import { AppointmentCardMenu } from './AppointmentCardMenu'
import { isToday } from './utils'
import type { Appointment, AppointmentCancelation } from './PatientAppointmentsService'

const FULL_DATE_STR_FORMAT = 'MMM d, yyyy, h:mma'
const TIME_OF_DAY_STR_FORMAT = 'h:mma'
const DATE_STR_FORMAT = 'MMM d, yyyy'

const STATUS_PILLS: Record<string, { tone: Tone; icon: ReactNode }> = {
  'Checked Out': { tone: 'success', icon: <CheckIcon sx={{ fontSize: '1.4rem' }} /> },
  'Not Seen': { tone: 'neutral', icon: <VisibilityOffIcon sx={{ fontSize: '1.4rem' }} /> },
  Scheduled: { tone: 'info', icon: <CalendarTodayIcon sx={{ fontSize: '1.3rem' }} /> },
}

/**
 * Design-refresh appointment card: the legacy card's data (type, provider,
 * concerns, forms, cancelation details, booked-by trail) re-rendered as a
 * SectionCard — DetailGrid body, toned status pills, and the date/booked-by
 * strip as the card footer. Action wiring (menu, cancel flow, join, copy
 * link) is identical to the legacy card.
 */
export const AppointmentCard: FC<{
  appointment: Appointment | AppointmentCancelation
  patientId: number
  selectAppointmentForCancel: (appointment: Appointment | AppointmentCancelation) => void
  isCanceled?: boolean
}> = props => {
  const { appointment, isCanceled } = props
  const theme = useTheme()

  // `appointment` is a union of two divergent BFF shapes (scheduled vs. cancelation).
  // Narrow on `canceled_at` so each arm's exclusive fields are accessed type-safely.
  const canceledAppt = 'canceled_at' in appointment ? appointment : undefined
  const scheduledAppt = 'canceled_at' in appointment ? undefined : appointment

  const canceledBy =
    canceledAppt?.canceled_by == 'patient'
      ? 'patient'
      : (canceledAppt?.canceled_by_provider_name ?? 'Unknown')

  const { setActiveVideoVisitId } = useActiveVideoVisit()
  const { enqueueSnackbar } = useSnackbar()
  const today = isToday(appointment)
  const showStatus = (!today || scheduledAppt?.status !== 'Scheduled') && !isCanceled

  const copyMemberVideoLinkToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(`${membersWebAppUrl}/visit/${appointment.id}`)
      enqueueSnackbar('Copied member video link to clipboard', { variant: 'success' })
    } catch {
      enqueueSnackbar('Could not copy the member video link', { variant: 'error' })
    }
  }

  const canceledDate = canceledAppt
    ? format(new Date(canceledAppt.canceled_at), FULL_DATE_STR_FORMAT)
    : ''
  const canceledByDetails = `Canceled by ${canceledBy} ${canceledDate}`

  const fireflyReasons = canceledAppt?.cancelation_reasons?.firefly_reasons?.length
    ? `Provider reason: ${canceledAppt.cancelation_reasons.firefly_reasons.join(', ')}`
    : undefined

  const patientReasons = canceledAppt?.cancelation_reasons?.patient_reasons?.length
    ? `Patient reason: ${canceledAppt.cancelation_reasons.patient_reasons.join(', ')}`
    : undefined

  const toolTipTitle = [canceledByDetails, fireflyReasons, patientReasons].join('\n')
  // The type is unset when the appointment's reason has no matching type row.
  const label = appointment.appointment_type?.name ?? appointment.reason

  const details: DetailGridItem[] = [
    {
      label: 'Type',
      value: (
        <Box component="span" sx={{ fontWeight: 600, fontSize: '1.4rem' }}>
          {label} ({appointment.duration_minutes} min)
        </Box>
      ),
    },
    { label: 'With', value: appointment.provider_name },
    {
      label: 'For',
      value: (
        <>
          {scheduledAppt?.symptoms_summary ? (
            <Box component="span" sx={{ display: 'block' }}>
              {scheduledAppt.symptoms_summary}
            </Box>
          ) : null}
          <Box component="span" sx={{ display: 'block' }}>
            {scheduledAppt?.description || '-'}
          </Box>
        </>
      ),
    },
    ...(scheduledAppt?.chat_thread_case_id != null
      ? [
          {
            label: 'Booked from',
            value: (
              <Box
                component={Link}
                to={`/patients/${props.patientId}/todos?caseId=${scheduledAppt.chat_thread_case_id}`}
                sx={{ color: 'primary.main' }}
              >
                Chat thread
              </Box>
            ),
          },
        ]
      : []),
    ...(scheduledAppt && scheduledAppt.form_submissions.length > 0
      ? [
          {
            label: 'Form',
            value: (
              <>
                {scheduledAppt.form_submissions.map(formSubmission => (
                  <Box key={formSubmission.id} component="span" sx={{ display: 'block' }}>
                    {formSubmission.form_title}:{' '}
                    <Box
                      component={Link}
                      to={`/patients/${props.patientId}/forms/submissions/${formSubmission.id}`}
                      sx={{ color: 'primary.main' }}
                    >
                      {getFormSubmissionStatus({
                        completedAt: formSubmission.completed_at,
                        expiredAt: formSubmission.expired_at,
                        startedAt: formSubmission.started_at,
                      })}
                    </Box>
                  </Box>
                ))}
              </>
            ),
          },
        ]
      : []),
  ]

  const statusPill = scheduledAppt?.status ? STATUS_PILLS[scheduledAppt.status] : undefined

  const footer = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CalendarTodayIcon sx={{ fontSize: '1.5rem', color: 'text.secondary' }} />
        <Box component="span" sx={{ fontSize: '1.3rem', color: 'text.primary' }}>
          {format(new Date(appointment.start), DATE_STR_FORMAT)}
        </Box>
        <Box component="span" sx={{ fontSize: '1.3rem', color: 'text.primary' }}>
          {format(new Date(appointment.start), TIME_OF_DAY_STR_FORMAT)}&nbsp;–&nbsp;
          {format(new Date(appointment.end), TIME_OF_DAY_STR_FORMAT)}
        </Box>
      </Box>
      <Box component="span" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
        Booked by&nbsp;
        {(appointment.scheduled_by == 'patient'
          ? 'patient'
          : appointment.scheduled_by_provider_name) ?? 'Unknown'}
        {appointment.scheduled_at ? (
          <>,&nbsp;{format(new Date(appointment.scheduled_at), FULL_DATE_STR_FORMAT)}</>
        ) : null}
      </Box>
    </Box>
  )

  return (
    <SectionCard
      sx={{ mb: 2 }}
      footer={footer}
      accentColor={
        isCanceled
          ? theme.palette.tones.error.fg
          : today
            ? theme.palette.tones.warning.fg
            : undefined
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <DetailGrid items={details} columns={2} />
        </Box>
        {/* Fixed actions column: never inside the wrapping detail rows. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {today && !isCanceled ? (
            <>
              <StatusPill
                tone="warning"
                icon={<TimerIcon sx={{ fontSize: '1.4rem' }} />}
                label="Today"
              />
              <Tooltip title="Copy Member Video Link">
                <IconButton
                  size="small"
                  aria-label="Copy member video link"
                  onClick={() => void copyMemberVideoLinkToClipboard()}
                >
                  <ContentCopyIcon sx={{ fontSize: '1.8rem' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open in new tab">
                <IconButton
                  size="small"
                  aria-label="Open visit in new tab"
                  component={Link}
                  target="_blank"
                  rel="noopener noreferrer"
                  to={`/visit/${appointment.id}`}
                >
                  <LaunchIcon sx={{ fontSize: '1.8rem' }} />
                </IconButton>
              </Tooltip>
            </>
          ) : null}
          {showStatus && scheduledAppt?.status ? (
            <StatusPill
              tone={statusPill?.tone ?? 'neutral'}
              icon={statusPill?.icon}
              label={scheduledAppt.status}
            />
          ) : null}
          {isCanceled ? (
            <>
              <StatusPill
                tone="error"
                icon={<BlockIcon sx={{ fontSize: '1.4rem' }} />}
                label={`Canceled by ${canceledBy}`}
              />
              <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{toolTipTitle}</Box>}>
                <InfoIcon sx={{ fontSize: '1.8rem', color: 'text.secondary' }} />
              </Tooltip>
            </>
          ) : null}
          <AppointmentCardMenu
            appointment={appointment}
            patientId={props.patientId}
            selectAppointmentForCancel={props.selectAppointmentForCancel}
          />
          {today && !isCanceled ? (
            <Button
              size="small"
              variant="contained"
              onClick={() => setActiveVideoVisitId(appointment.id)}
            >
              Join
            </Button>
          ) : null}
        </Box>
      </Box>
    </SectionCard>
  )
}
